/**
 * «Din påvirkning» — aggregert fotavtrykk for én bruker (UX-prinsipp #4 Endowment,
 * grep B — se docs/plan-ux-principles-retention.md). Gjør akkumulert eierskap synlig
 * og verdsatt: plassene DU la til, hvor mye de brukes, hvor du har vært. Ærlig verdi —
 * hvert tall er noe brukeren faktisk har skapt eller gjort, ikke en kunstig teller.
 *
 * Ren og beregnes klientside over brukerens egne data (besøk + egne plasser), så den
 * er fullt testbar og trenger ingen backend-aggregering.
 */

export interface ImpactCreatedSpot {
  visitCount?: number;
  isVerified?: boolean;
  county?: string;
}

export interface ImpactVisit {
  spotId: string;
  county?: string;
  visitedAt: number;
  checkedOutAt?: number;
}

export interface UserImpact {
  /** Antall plasser brukeren har lagt til. */
  spotsCreated: number;
  /** Av disse: hvor mange er bekreftet. */
  spotsCreatedVerified: number;
  /** Sum av besøk på plassene brukeren la til — «brukt N ganger av reisende». */
  spotsCreatedUseCount: number;
  /** Unike plasser brukeren har sjekket inn på. */
  spotsVisited: number;
  /** Distinkte fylker brukeren har besøkt (ikke-tomme). */
  countiesVisited: number;
  /** Sum netter fra fullførte opphold (innsjekk med utsjekk), minst 1 per opphold. */
  nights: number;
  /** Har brukeren noe å vise? Brukes til å skjule flaten for helt nye brukere. */
  hasAny: boolean;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function stayNights(visitedAt: number, checkedOutAt?: number): number {
  if (!checkedOutAt || checkedOutAt <= visitedAt) return 0;
  return Math.max(1, Math.round((checkedOutAt - visitedAt) / MS_PER_DAY));
}

export function computeUserImpact(
  createdSpots: ImpactCreatedSpot[] = [],
  visits: ImpactVisit[] = [],
): UserImpact {
  const spotsCreated = createdSpots.length;
  const spotsCreatedVerified = createdSpots.filter(s => s.isVerified).length;
  const spotsCreatedUseCount = createdSpots.reduce((sum, s) => sum + Math.max(0, s.visitCount || 0), 0);

  const spotsVisited = new Set(visits.map(v => v.spotId)).size;
  const countiesVisited = new Set(
    visits.map(v => (v.county || '').trim()).filter(Boolean),
  ).size;
  const nights = visits.reduce((sum, v) => sum + stayNights(v.visitedAt, v.checkedOutAt), 0);

  const hasAny = spotsCreated > 0 || spotsVisited > 0;

  return {
    spotsCreated,
    spotsCreatedVerified,
    spotsCreatedUseCount,
    spotsVisited,
    countiesVisited,
    nights,
    hasAny,
  };
}
